/**
 * Store-review nudge policy — pure decision logic, fully unit-tested.
 *
 * Philosophy: ask at the peak of the product loop (a weekly wrap-up with
 * real completions on the board), never before trust exists, and never
 * often enough to feel like nagging. The OS quota (iOS ~3 prompts/year)
 * is a backstop, not the plan.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** User must have had the app at least this long before any ask. */
export const REVIEW_MIN_TENURE_MS = 7 * DAY_MS;
/** Minimum completions in the closed week — only ask after a GOOD week. */
export const REVIEW_MIN_COMPLETED = 3;
/** Cool-down between asks. */
export const REVIEW_COOLDOWN_MS = 60 * DAY_MS;
/** Lifetime cap on asks. */
export const REVIEW_MAX_ASKS = 3;

export interface ReviewSignals {
  /** First-run timestamp on this device. */
  installedAt: number;
  /** How many times we've asked before. */
  askCount: number;
  /** When we last asked (0 = never). */
  lastAskAt: number;
  /** Completions in the week that just closed. */
  completedThisWeek: number;
}

export function shouldAskForReview(s: ReviewSignals, now: number = Date.now()): boolean {
  if (s.askCount >= REVIEW_MAX_ASKS) return false;
  if (s.installedAt <= 0 || now - s.installedAt < REVIEW_MIN_TENURE_MS) return false;
  if (s.lastAskAt > 0 && now - s.lastAskAt < REVIEW_COOLDOWN_MS) return false;
  return s.completedThisWeek >= REVIEW_MIN_COMPLETED;
}
